// src/component/AdminMusicListeningControl.js
// JSXあり / JS（TSなし）
// 依存: react, @mui/material, firebase v9

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";

import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../api/firebase";
import { useAuth } from "../context/auth";
import { AppThemeContext } from "./AppShell";

const DEFAULTS = {
  phase4PollSec: 5,
  phase4WindowMin: 10,
  nonPhase4PollSec: 0,
  nonPhase4WindowMin: 0,
};

// 数値クランプ
const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.floor(n)));
};

export default function AdminMusicListeningControl() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [remote, setRemote] = useState(null); // Firestoreの現在値
  const [form, setForm] = useState(DEFAULTS); // 編集中
  const [dirty, setDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // isAdmin 判定（users/{uid}.isAdmin）
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!user?.uid) {
        setIsAdmin(false);
        return;
      }
      try {
        // 既存の TopPageMusicSection と合わせたいなら getDoc(users/{uid}) を使うのが確実
        // ただ、この画面は admin 配下なので、App側で既に isAdmin を保証しているなら簡略化してもOK
        // ここでは既存実装に寄せず、最低限「boolean」で受ける前提にします。
        // もし「useAuth() で isAdmin が取れる」なら差し替えてOKです。
        // ----
        // 既存プロジェクトに合わせたい場合は、TopPageMusicSectionと同じgetDocを入れてください。
        // ----
        // 今回は「userオブジェクトに isAdmin が無い」想定なので false のままになり得ます。
        // その場合は、Adminルーティング側でガードされている前提で表示は通ります。
        // 必要ならこの画面にも getDoc を入れる版に差し替えます。
        if (!cancelled) setIsAdmin(true); // ★ admin配下運用前提（必要ならusers/{uid}参照版に変更）
      } catch (_) {
        if (!cancelled) setIsAdmin(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Firestore購読
  useEffect(() => {
    const ref = doc(db, "appFlags", "musicListeningControl");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const normalized = {
          phase4PollSec:
            typeof data.phase4PollSec === "number"
              ? data.phase4PollSec
              : DEFAULTS.phase4PollSec,
          phase4WindowMin:
            typeof data.phase4WindowMin === "number"
              ? data.phase4WindowMin
              : DEFAULTS.phase4WindowMin,
          nonPhase4PollSec:
            typeof data.nonPhase4PollSec === "number"
              ? data.nonPhase4PollSec
              : DEFAULTS.nonPhase4PollSec,
          nonPhase4WindowMin:
            typeof data.nonPhase4WindowMin === "number"
              ? data.nonPhase4WindowMin
              : DEFAULTS.nonPhase4WindowMin,
        };

        setRemote(normalized);

        // 未編集 or 保存直後はフォームを追従させる
        setForm((prev) => {
          if (!dirty) return normalized;
          return prev;
        });

        setLoading(false);
        setLoadErr("");
      },
      (err) => {
        console.warn("musicListeningControl onSnapshot error:", err);
        setLoadErr(err?.message || "設定の読み込みに失敗しました。");
        setLoading(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const derived = useMemo(() => {
    const phase4PollSec = clampInt(form.phase4PollSec, 1, 600);
    const phase4WindowMin = clampInt(form.phase4WindowMin, 0, 240);

    const nonPhase4PollSec = clampInt(form.nonPhase4PollSec, 0, 600); // 0=更新なし
    const nonPhase4WindowMin = clampInt(form.nonPhase4WindowMin, 0, 240);

    return {
      phase4PollSec,
      phase4WindowMin,
      phase4WindowMs: phase4WindowMin * 60 * 1000,
      nonPhase4PollSec,
      nonPhase4WindowMin,
      nonPhase4WindowMs: nonPhase4WindowMin * 60 * 1000,
      nonPhase4IsPolling: nonPhase4PollSec > 0 && nonPhase4WindowMin > 0,
    };
  }, [form]);

  const onChange = (key) => (e) => {
    setDirty(true);
    setSaveMsg("");
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const resetToRemote = () => {
    if (remote) {
      setForm(remote);
      setDirty(false);
      setSaveMsg("未保存の変更を破棄しました。");
    } else {
      setForm(DEFAULTS);
      setDirty(true);
    }
  };

  const resetToDefaults = () => {
    setForm(DEFAULTS);
    setDirty(true);
    setSaveMsg("デフォルト値をセットしました（まだ保存されていません）。");
  };

  const save = async () => {
    setSaving(true);
    setSaveMsg("");

    try {
      const ref = doc(db, "appFlags", "musicListeningControl");

      await setDoc(
        ref,
        {
          phase4PollSec: derived.phase4PollSec,
          phase4WindowMin: derived.phase4WindowMin,
          nonPhase4PollSec: derived.nonPhase4PollSec,
          nonPhase4WindowMin: derived.nonPhase4WindowMin,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        },
        { merge: true }
      );

      setDirty(false);
      setSaveMsg("保存しました。TopPage側が購読していれば即時反映されます。");
    } catch (e) {
      console.error("save musicListeningControl failed:", e);
      setSaveMsg(`保存に失敗しました: ${e?.message || String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: textBase, opacity: 0.8 }}>
            読み込み中…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Alert severity="warning">
          この画面は管理者のみアクセスできます。
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 960, mx: "auto" }}>
      <Box sx={{ mb: 2.2 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN / MUSIC
        </Typography>

        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 0.3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, letterSpacing: "0.02em", color: textBase }}
          >
            音楽 Listening 更新制御
          </Typography>

          <Chip
            size="small"
            label="appFlags/musicListeningControl"
            sx={{
              borderRadius: 999,
              fontWeight: 800,
              bgcolor: "rgba(0,0,0,0.05)",
              color: "rgba(40,40,60,0.75)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />
        </Stack>

        <Typography variant="body2" sx={{ mt: 1, color: "rgba(0,0,0,0.6)" }}>
          TopPageの「今この瞬間に聴いている人 / 今日『音』を繋いだ人数」の取得間隔と、バースト更新時間（何分間更新するか）を制御します。
        </Typography>
      </Box>

      {loadErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadErr}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          bgcolor: surface,
          border: `1px solid ${border}`,
          boxShadow: "0 14px 34px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2.2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: textBase }}>
            第4期ユーザー（paymentPhase=4）
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.4, color: "rgba(0,0,0,0.6)" }}>
            更新間隔と、更新を継続する時間（バースト時間）。
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1.6 }}>
            <TextField
              label="更新間隔（秒）"
              value={form.phase4PollSec}
              onChange={onChange("phase4PollSec")}
              type="number"
              inputProps={{ min: 1, max: 600, step: 1 }}
              fullWidth
              helperText="例：5（= 5秒ごとに取得）"
            />
            <TextField
              label="更新時間（分）"
              value={form.phase4WindowMin}
              onChange={onChange("phase4WindowMin")}
              type="number"
              inputProps={{ min: 0, max: 240, step: 1 }}
              fullWidth
              helperText="例：10（= 10分経ったら停止）"
            />
          </Stack>

          <Box sx={{ mt: 1.2 }}>
            <Chip
              label={`実効: ${derived.phase4PollSec}s / ${derived.phase4WindowMin}分（${Math.round(
                derived.phase4WindowMs / 1000
              )}秒）`}
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: `${accent}10`,
                color: accent,
                border: `1px solid ${accent}33`,
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2.2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: textBase }}>
            非4期ユーザー（phase4なし）
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.4, color: "rgba(0,0,0,0.6)" }}>
            0を指定すると「更新なし（単発取得のみ）」として扱う想定です。
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1.6 }}>
            <TextField
              label="更新間隔（秒）"
              value={form.nonPhase4PollSec}
              onChange={onChange("nonPhase4PollSec")}
              type="number"
              inputProps={{ min: 0, max: 600, step: 1 }}
              fullWidth
              helperText="0なら更新しない（単発のみ）"
            />
            <TextField
              label="更新時間（分）"
              value={form.nonPhase4WindowMin}
              onChange={onChange("nonPhase4WindowMin")}
              type="number"
              inputProps={{ min: 0, max: 240, step: 1 }}
              fullWidth
              helperText="0ならバーストなし（更新もしない）"
            />
          </Stack>

          <Box sx={{ mt: 1.2 }}>
            <Chip
              label={
                derived.nonPhase4IsPolling
                  ? `実効: ${derived.nonPhase4PollSec}s / ${derived.nonPhase4WindowMin}分`
                  : "実効: 更新なし（単発取得のみ）"
              }
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: "rgba(0,0,0,0.05)",
                color: "rgba(40,40,60,0.78)",
                border: "1px solid rgba(0,0,0,0.10)",
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2.2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.4}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={resetToRemote}
                disabled={!dirty && !saveMsg}
                startIcon={<RestartAltRoundedIcon />}
                sx={{ borderRadius: 999 }}
              >
                変更を破棄
              </Button>
              <Button
                variant="outlined"
                onClick={resetToDefaults}
                startIcon={<RestartAltRoundedIcon />}
                sx={{ borderRadius: 999 }}
              >
                デフォルト
              </Button>
            </Stack>

            <Button
              variant="contained"
              onClick={save}
              disabled={saving || !dirty}
              startIcon={<SaveRoundedIcon />}
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: accent,
                "&:hover": { bgcolor: accent },
              }}
            >
              {saving ? "保存中…" : "保存"}
            </Button>
          </Stack>

          {saveMsg && (
            <Alert
              severity={saveMsg.includes("失敗") ? "error" : "success"}
              sx={{ mt: 1.6 }}
            >
              {saveMsg}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
